import { useEffect, useRef, useState } from 'react';

// https://github.com/kentcdodds/react-hooks/blob/1cb2e7664502b4c3f088f54d9a80dbefe171882b/src/utils.js

type Props<T> = {
  key: string;
  defaultValue: T;
  opts?: {
    serialize?: (value: T) => string;
    deserialize?: (value: string) => T;
  };
};

export function useLocalStorage<T>({
  key,
  defaultValue,
  opts: { serialize = JSON.stringify, deserialize = JSON.parse } = {},
}: Props<T>): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState(() => {
    const valueInLocalStorage = window.localStorage.getItem(key);
    if (valueInLocalStorage) {
      try {
        return deserialize(valueInLocalStorage);
      } catch (error) {
        window.localStorage.removeItem(key);
      }
    }
    return defaultValue;
  });

  const prevKeyRef = useRef(key);

  useEffect(() => {
    const prevKey = prevKeyRef.current;
    if (prevKey !== key) {
      window.localStorage.removeItem(prevKey);
    }
    prevKeyRef.current = key;
    window.localStorage.setItem(key, serialize(state));
  }, [key, state, serialize]);

  return [state, setState];
}
